import { Box, Tooltip, Typography } from "@mui/material";
import { format } from "date-fns";
import type React from "react";

import { Spacing } from "../types/enum";

/**
 * How one tool's headline rate moved over the period.
 *
 * A rate, not a volume: the share of the members reached in each bucket who did
 * the thing the tool exists for. Volume is already on the funnel; what this
 * answers is whether the tool is getting better or worse at its job, which a
 * count can't say — a bigger send lifts every count without anything improving.
 *
 * One series, one hue, so no legend: the heading names it. The last bucket is
 * always faded, because members reached days ago have had no time to do
 * anything and a fresh low bar is not a decline.
 */

const BAR_COLOR = "#00583D";
const TRACK_COLOR = "rgba(0,0,0,0.04)";
const CHART_HEIGHT = 56;

export interface TrendPoint {
  bucketStart: number;
  reached: number;
  sends: number;
  responded: number;
  attendedAny: number;
  partial: boolean;
}

const OutreachSourceTrend: React.FC<{
  points: TrendPoint[];
  /** Which of the two rates to plot, and what to call it. */
  metric: "responded" | "attendedAny";
  label: string;
}> = ({ points, metric, label }) => {
  if (points.length < 2) {
    return null;
  }

  return (
    <Box display="flex" flexDirection="column" gap={Spacing.xs}>
      <Typography variant="caption" color="textSecondary">
        {label} — share of the members reached each week, on one 0–100% scale.
      </Typography>

      <Box display="flex" alignItems="flex-end" gap={Spacing.xs} sx={{ height: CHART_HEIGHT }}>
        {points.map((point) => {
          const rate = point.reached > 0 ? point[metric] / point.reached : 0;
          const height = rate > 0 ? Math.max(rate * CHART_HEIGHT, 3) : 0;
          const when = format(new Date(point.bucketStart), "d MMM yyyy");
          const title =
            point.reached === 0
              ? `${when} — nobody reached`
              : `${when} — ${point[metric]} of ${point.reached} (${Math.round(rate * 100)}%)${
                  point.partial ? " · this week isn't finished, so it always reads low" : ""
                }`;
          return (
            <Tooltip key={point.bucketStart} title={title}>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 6,
                  height: CHART_HEIGHT,
                  display: "flex",
                  alignItems: "flex-end",
                  bgcolor: TRACK_COLOR,
                  borderRadius: 1,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height,
                    bgcolor: BAR_COLOR,
                    opacity: point.partial ? 0.35 : 1,
                    borderRadius: 1,
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      <Box display="flex" justifyContent="space-between">
        <Typography variant="caption" color="textSecondary" sx={{ fontSize: 10 }}>
          {format(new Date(points[0].bucketStart), "d MMM")}
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ fontSize: 10 }}>
          {format(new Date(points[points.length - 1].bucketStart), "d MMM")}
        </Typography>
      </Box>
    </Box>
  );
};

export default OutreachSourceTrend;
